import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/useAuthStore';
import { Toaster } from 'react-hot-toast';
import socketService from './services/socketService';

import ProtectedRoute from './shared/components/ProtectedRoute';
import Loading from './shared/components/Loading';

/**
 * Carga diferida por ruta (Ola 9, punto 9-01).
 *
 * Antes todo el codigo viajaba en un unico paquete de 4.194 kB, de modo que la
 * pantalla de acceso descargaba tambien el dashboard, la zona de usuario y el
 * motor 3D que solo usa uno de sus paneles.
 *
 * La pagina de inicio NO se difiere: es lo primero que ve cualquiera y
 * diferirla solo anadiria un parpadeo.
 */
import HomePage from './pages/HomePage';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UserHomePage = lazy(() => import('./pages/UserHomePage'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

function App() {
  const { 
    checkAuth, 
    isLoading: isAuthLoading, 
    user,
    isInitialized,
    initialize,
    authError,
    clearAuthError
  } = useAuthStore();
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [isInitialized, initialize]);

  useEffect(() => {
    if (isInitialized) {
      checkAuth();
    }
  }, [checkAuth, isInitialized]);

  useEffect(() => {
    if (user) {
      socketService.ensureConnection();
      
      const connectionCheck = setInterval(() => {
        if (!socketService.isConnected()) {
          socketService.forceReconnect();
        }
      }, 30000);

      return () => {
        clearInterval(connectionCheck);
      };
    } else {
      socketService.disconnect();
    }
  }, [user]);

  // Eliminado el efecto que reconectaba el socket al entrar en /home-users
  // (Ola 9, punto 9-11). Dependia de `window.location.pathname`, que no es
  // estado reactivo: React no puede detectar sus cambios, de modo que el efecto
  // se reevaluaba en cada render dejando un setTimeout sin cancelar cada vez.
  // El efecto anterior ya garantiza la conexion mientras haya usuario.

  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        clearAuthError();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [authError, clearAuthError]);

  if (!isInitialized || isAuthLoading) {
    return <Loading />;
  }

  return (
    <>
      <Toaster
        position="top-center"
        containerStyle={{
          top: 20,
          zIndex: 9999
        }}
        toastOptions={{
          duration: 3000,
          style: {
            background: '#374151',
            color: '#fff',
            fontSize: '12px',
            fontWeight: '500',
            borderRadius: '6px',
            padding: '8px 12px',
            maxWidth: '280px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          },
          success: {
            duration: 2000,
            style: {
              background: '#059669',
              color: '#fff',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#059669',
            },
          },
          error: {
            duration: 4000,
            style: {
              background: '#DC2626',
              color: '#fff',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#DC2626',
            },
          },
          loading: {
            style: {
              background: '#2563EB',
              color: '#fff',
            },
          },
        }}
      />
      
      {/*
        El fallback usa el mismo indicador de carga que el resto de la
        aplicacion (punto 9-05): un hueco en blanco durante la descarga de la
        ruta se lee como un fallo, no como una espera.
      */}
      <Suspense fallback={<Loading message="Cargando..." />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<HomePage />} />

          <Route element={<ProtectedRoute allowedRoles={['Superuser']} />}>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>

          <Route element={<ProtectedRoute allowedRoles={['Admin', 'Editor', 'Viewer']} />}>
            <Route path="/home-users" element={<UserHomePage />} />
          </Route>

          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="*" element={<h1>404 - Not Found</h1>} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;